import { DocumentLocationAnchor } from '../collab/DocumentLocationAnchor';
import { buildDocumentDesktopLink } from '../collab/documentDesktopLink';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  asTeamDocumentId,
  asTeamProjectId,
} from '@nimbalyst/collab-bundle/editor';
import { SharedDocsListView, useCollabPagesState, useSharedDocumentTitles } from '@nimbalyst/collab-bundle/docs-ui';
import { CollabDocsShell, ProjectScopeSummary, ProjectSwitcher, useDocsShell } from '../collab/CollabDocsShell';
import { EDITOR_LOADING, EDITOR_SURFACE } from '../collab/editorSurfaceStyles';
import { CollabLoadingPanel, PanelState } from '../components/Chrome';
import { NothingSharedYet } from '../components/NothingSharedYet';
import { CollabEditorMount } from '../collab/CollabEditorMount';
import { DesktopHandoffPanel } from '../collab/DesktopHandoffPanel';
import { useDocumentComments } from '../collab/documentComments';
import { DocumentWorkspace } from '../collab/DocumentWorkspace';
import { ExtensionEditorMount } from '../collab/ExtensionEditorMount';
import type { CollabDocumentPresentation } from '../collab/CollabDocumentSurface';
import type { EditorViewport } from '@nimbalyst/collab-bundle/editor';
import { getBrowserExtension } from '@nimbalyst/web-editors/registry';
import { useSharedDocumentType } from '../collab/sharedDocumentType';
import {
  canOrgRoleAuthorComments,
  getOrgCallerRole,
  getOrgRoster,
  loadOrgRoster,
  memberDisplayName,
} from '../collab/orgRoster';
import { consoleConfig } from '../config';
import { getProjectPersistenceId, getProjectRouteId, rememberLastUsedProjectId } from '../projectSelection';
import { legacyWikiRedirectPath, orgDocsPath, orgDocumentPath, orgFolderPath, orgTypePagePath, orgWikiPath } from '../routing';
import { trackOpenPageEditor } from '../pages/openPageEditors';
import { pagesLanding } from '../pages/pagesHome';
import { pagesPlacedViewTypes } from '../pages/placedViewTypes';
import type { OrganizationRouteContext } from './workspaceContext';
import { EmbeddedEditBar, useEmbeddedEditMode } from '../embedded/EmbeddedEditMode';
import { isEmbedded } from '../embedded/embedMarker';
import { useOptionalProjectTrackerSession } from '../trackers/ProjectTrackerSession';

export type DocsSurfaceErrorKind =
  | 'no-access'
  | 'removed-member'
  | 'deleted-document'
  | 'session-expired'
  | 'service-unavailable'
  | 'unsupported-document-type';

export type DocsRouteDescriptor =
  | { kind: 'home' }
  | { kind: 'folder'; folderId: string }
  | { kind: 'document'; projectId: string; documentId: string };

const docsErrorCopy: Record<DocsSurfaceErrorKind, { title: string; message: string }> = {
  'no-access': {
    title: 'No access to this organization',
    message: 'This link identifies an organization you cannot currently open.',
  },
  'removed-member': {
    title: 'Organization access was removed',
    message: 'Your membership is no longer active. Live document access has been stopped.',
  },
  'deleted-document': {
    title: 'Document not found',
    message: 'This document was deleted or is no longer available to your project membership.',
  },
  'session-expired': {
    title: 'Your session expired',
    message: 'Sign in again before loading shared organization data.',
  },
  'service-unavailable': {
    title: 'Shared documents are unavailable',
    message: 'The collaboration service could not be reached. Your route has been preserved.',
  },
  'unsupported-document-type': {
    title: 'Unsupported document type',
    message: 'This browser workspace cannot open this document type yet.',
  },
};

export function DocsSurfaceError({ kind, detail }: { kind: DocsSurfaceErrorKind; detail?: string }) {
  const copy = docsErrorCopy[kind];
  return (
    <PanelState tone="error" className="docs-state-panel w-full" role="alert" data-docs-state={kind}>
      <strong>{copy.title}</strong>
      <span>{detail || copy.message}</span>
    </PanelState>
  );
}

export function DocsWorkspaceRoute() {
  const context = useOutletContext<OrganizationRouteContext>();
  const navigate = useNavigate();
  const { projectId, folderId, documentId, itemRef, typeId } = useParams<{
    projectId?: string;
    folderId?: string;
    documentId?: string;
    itemRef?: string;
    typeId?: string;
  }>();
  // `navigate` itself is a new function after every location change, so this
  // callback is not stable and nothing downstream may key on its identity —
  // the shell reads it live rather than memoizing its host over it.
  const navigateTo = useCallback((path: string) => navigate(path), [navigate]);
  const selectProject = useCallback((project: typeof context.team.projects[number]) => {
    rememberLastUsedProjectId(context.team.orgId, getProjectPersistenceId(project));
    navigate(orgDocsPath(context.team, getProjectRouteId(project)));
  }, [context.team, navigate]);
  // Not an error: an org with no project you can open is what a membership
  // looks like before anyone has shared into it. `no-access` is reserved for a
  // link to an org you genuinely may not open.
  if (!context.project) {
    return (
      <NothingSharedYet
        orgName={context.team.name}
        orgId={context.team.orgId}
        getTeamJwt={context.getTeamJwt}
      />
    );
  }
  const selectedProjectId = getProjectRouteId(context.project);
  if ((documentId || itemRef || typeId) && projectId !== selectedProjectId) {
    return <DocsSurfaceError kind="no-access" detail="The project in this link is no longer available to you." />;
  }
  if (!projectId || projectId !== selectedProjectId) {
    return <Navigate to={orgDocsPath(context.team, selectedProjectId)} replace />;
  }
  return (
    <CollabDocsShell
      team={context.team}
      project={context.project}
      teamSession={context.teamSession}
      getTeamJwt={context.getTeamJwt}
      serverUrl={consoleConfig.collabWebSocketBaseUrl}
      navigate={navigateTo}
      onSelectProject={selectProject}
      activeDocumentId={documentId}
      activeFolderId={folderId}
      activeItemRef={itemRef}
      activeTypeId={typeId}
    >
      <Outlet context={context} />
    </CollabDocsShell>
  );
}

/**
 * All pages: every page of the project as a list, reached from the header.
 * New pages come from the header's New control, as on the desktop.
 */
function DocsListRoute({ folderId }: { folderId: string | null }) {
  const { team, project, projectId, navigate, onSelectProject } = useDocsShell();
  return (
    <SharedDocsListView
      folderId={folderId}
      onSelectFolder={(nextFolderId) => navigate(orgFolderPath(team, projectId, nextFolderId))}
      title={(
        <div className="docs-list-project flex min-w-0 flex-col">
          <ProjectSwitcher team={team} project={project} projects={team.projects} onSelectProject={onSelectProject} />
          <span className="truncate text-[11px] text-nim-faint"><ProjectScopeSummary team={team} /></span>
        </div>
      )}
    />
  );
}

/** The project opens on its Home page (Decision 28); see `pagesLanding`. */
export function DocsHomeRoute() {
  const { team, projectId } = useDocsShell();
  const { documents, syncStatus } = useCollabPagesState();
  const landing = pagesLanding(projectId, documents, syncStatus);
  if (landing.kind === 'home') return <Navigate to={orgDocumentPath(team, projectId, landing.documentId)} replace />;
  if (landing.kind === 'pending') return <CollabLoadingPanel title="Opening Home" detail="Reading your team's pages." />;
  return <DocsListRoute folderId={null} />;
}

export function AllPagesRoute() {
  return <DocsListRoute folderId={null} />;
}

/** Folders are pages now; an old folder link opens the page. */
export function DocsFolderRoute() {
  const { team, projectId } = useDocsShell();
  const { folderId } = useParams<{ folderId: string }>();
  if (!folderId) return <Navigate to={orgWikiPath(team, projectId)} replace />;
  return <Navigate to={orgDocumentPath(team, projectId, folderId)} replace />;
}

/** A shared link from before the Wiki had its own addresses (`legacyWikiRedirectPath`). */
export function LegacyWikiRedirect() {
  const location = useLocation();
  const target = legacyWikiRedirectPath(location.pathname, `${location.search}${location.hash}`);
  return target ? <Navigate to={target} replace /> : null;
}

const TYPE_PAGE_PREFIX = 'type-page:';

export function DocumentRoute() {
  const { projectId, documentId } = useParams<{ projectId: string; documentId: string }>();
  const { team } = useOutletContext<OrganizationRouteContext>();
  if (!projectId || !documentId) return <DocsSurfaceError kind="deleted-document" detail="The document route is incomplete." />;
  // A type's prose only renders on its type page, above the table.
  if (documentId.startsWith(TYPE_PAGE_PREFIX)) {
    return <Navigate to={orgTypePagePath(team, projectId, documentId.slice(TYPE_PAGE_PREFIX.length))} replace />;
  }
  return <DocumentEditorRoute projectId={projectId} documentId={documentId} />;
}

/**
 * Start the shared roster request at route mount, before a comment composer can
 * snapshot it.
 *
 * The response carries the caller's own org role alongside the roster, and that
 * role is what decides whether this user may author comments. It is reported
 * here rather than looked up in `members` so an org whose roster omits the
 * caller still gets an answer.
 */
export function useOrgRoster(
  orgId: string,
  getTeamJwt: OrganizationRouteContext['getTeamJwt'],
) {
  const [roster, setRoster] = useState(() => ({
    members: getOrgRoster(orgId),
    callerRole: getOrgCallerRole(orgId),
  }));
  useEffect(() => {
    let cancelled = false;
    void loadOrgRoster(orgId, getTeamJwt)
      .then((members) => {
        if (!cancelled) setRoster({ members, callerRole: getOrgCallerRole(orgId) });
      })
      .catch((error) => {
        // Presence is cosmetic: fall back to the id rather than block the editor.
        console.error('[DocsRoutes] Unable to resolve the presence identity', error);
      });
    return () => {
      cancelled = true;
    };
  }, [getTeamJwt, orgId]);
  return roster;
}

function DocumentEditorRoute({ projectId, documentId }: { projectId: string; documentId: string }) {
  const location = useLocation();
  const { teamSession, getTeamJwt } = useOutletContext<OrganizationRouteContext>();
  return (
    <SharedDocumentEditor
      projectId={projectId}
      documentId={documentId}
      teamSession={teamSession}
      getTeamJwt={getTeamJwt}
      search={location.search}
    />
  );
}

/**
 * One shared document, live, with comments, presence and the location anchor.
 *
 * The docs route's body, lifted so the Requests surface can show the document
 * a mention or reply points at in its own content column rather than sending
 * the reader to Shared Docs. Must render inside `CollabDocsUIProvider` for the
 * project the document lives in: the editor is chosen from that project's
 * index, and without it every document would open as rich text.
 */
export function SharedDocumentEditor({
  projectId,
  documentId,
  teamSession,
  getTeamJwt,
  search,
}: {
  projectId: string;
  documentId: string;
  teamSession: OrganizationRouteContext['teamSession'];
  getTeamJwt: OrganizationRouteContext['getTeamJwt'];
  /** The URL query: `blockId`, `threadId`, `commentId` scroll to their target. */
  search: string;
}) {
  const { members, callerRole } = useOrgRoster(teamSession.orgId, getTeamJwt);
  const self = members.find((member) => member.memberId === teamSession.memberId);
  // The member id is opaque, so prefer the Stytch roster name for presence.
  const presenceName = self ? memberDisplayName(self) : teamSession.memberId;
  const comments = useDocumentComments({
    orgId: teamSession.orgId,
    memberId: teamSession.memberId,
    presenceName,
    projectId,
    documentId,
    // Null until the roster lands, which reads as "not permitted" — the editor
    // mount re-reads this once it resolves rather than guessing in the gap.
    canComment: canOrgRoleAuthorComments(callerRole),
  });
  const source = useMemo(() => ({
    kind: 'team-room' as const,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    room: {
      orgId: teamSession.orgId,
      projectId: asTeamProjectId(projectId),
      documentId: asTeamDocumentId(documentId),
    },
    auth: {
      scope: 'team' as const,
      memberId: teamSession.memberId,
      getTeamJwt,
    },
  }), [documentId, getTeamJwt, projectId, teamSession.memberId, teamSession.orgId]);
  // The editor's presence label falls back from name to email to "Teammate".
  const user = useMemo(() => ({
    memberId: teamSession.memberId,
    name: presenceName,
    email: self?.email,
  }), [presenceName, self?.email, teamSession.memberId]);
  // Empty inside the phone app: every "Open in desktop" affordance is gated on it.
  const desktopHref = useMemo(
    () => (isEmbedded() ? '' : buildDocumentDesktopLink(documentId, teamSession.orgId, search, projectId)),
    [documentId, teamSession.orgId, search, projectId],
  );
  const resolution = useSharedDocumentType(teamSession.orgId, projectId, documentId);
  // In the phone app a page opens in Read mode; only a plain markdown page offers Edit.
  const editMode = useEmbeddedEditMode(
    documentId,
    resolution.state === 'resolved' && resolution.presentation.state === 'ready'
      && resolution.presentation.descriptor?.editor.kind === 'lexical',
  );
  const trackEmbeddedHandle = editMode.trackHandle;
  // Set type flushes an open page's last edits before it copies the page.
  const trackHandle = useCallback(
    (handle: Parameters<typeof trackOpenPageEditor>[1]) => {
      trackEmbeddedHandle(handle);
      return trackOpenPageEditor(documentId, handle);
    },
    [documentId, trackEmbeddedHandle],
  );
  // Inside an organization route this opts in even before the tracker room is
  // open (`null`); a harness with no project session leaves references key-only.
  const projectTrackers = useOptionalProjectTrackerSession();
  const trackerReferences = projectTrackers ? projectTrackers.references : undefined;

  // Use the session's reconciled display name, like the breadcrumb: a raw
  // index broadcast can carry an unreadable title after this tab has opened.
  const title = useSharedDocumentTitles().get(documentId)
    || (resolution.state === 'resolved' ? resolution.document.title : 'Shared document');
  useEffect(() => {
    const previous = document.title;
    document.title = `${title || 'Shared document'} · Nimbalyst`;
    return () => { document.title = previous; };
  }, [title]);
  return (
    <DocumentWorkspace>
      <DocumentLocationAnchor key={documentId} search={search} />
      <EmbeddedEditBar mode={editMode} />
      <DocumentEditorSurface
        readOnly={editMode.readOnly}
        resolution={resolution}
        source={source}
        user={user}
        comments={comments}
        desktopHref={desktopHref}
        trackerReferences={trackerReferences}
        onHandleChange={trackHandle}
      />
    </DocumentWorkspace>
  );
}

/**
 * Which editor opens this document.
 *
 * Every document used to be mounted in Lexical, which is correct for markdown
 * and silently wrong for everything else. The verdict comes from the same
 * catalog the tree and the list read, so a document that shows an "unsupported"
 * icon there cannot open as a blank rich-text document here.
 */
export function DocumentEditorSurface({
  resolution,
  source,
  user,
  comments,
  desktopHref,
  presentation,
  readOnly,
  onViewportRegistered,
  onHandleChange,
  renderDecisionArtifact,
  trackerReferences,
}: {
  renderDecisionArtifact?: Parameters<typeof CollabEditorMount>[0]['renderDecisionArtifact'];
  /** Live tracker references for a markdown body; see `CollabEditorMount`. */
  trackerReferences?: Parameters<typeof CollabEditorMount>[0]['trackerReferences'];
  resolution: ReturnType<typeof useSharedDocumentType>;
  source: Parameters<typeof CollabEditorMount>[0]['source'];
  user: Parameters<typeof CollabEditorMount>[0]['user'];
  comments: Parameters<typeof CollabEditorMount>[0]['comments'];
  desktopHref: string;
  /**
   * `inline` drops the page chrome, for a preview card or a detail popover.
   * The editor-kind dispatch below is identical either way, which is the
   * reason this surface is reused rather than copied.
   */
  presentation?: CollabDocumentPresentation;
  /**
   * Host-imposed read-only, forwarded to whichever editor this dispatch picks.
   * A canvas card is read-only until it is activated, and that has to hold
   * whether the card is markdown or an extension type.
   */
  readOnly?: boolean;
  /** Forwarded to an extension editor that publishes a scroll viewport. */
  onViewportRegistered?: (viewport: EditorViewport | null) => void;
  /** Development/test observer for the live collaborative surface. */
  onHandleChange?: Parameters<typeof CollabEditorMount>[0]['onHandleChange'];
}) {
  // The index has not answered yet. Guessing here would mount a rich-text
  // binding over a document whose real editor owns the Y.Doc layout.
  if (resolution.state === 'pending') {
    if (presentation === 'inline') {
      return <div className="collab-editor-inline-loading" data-connection-state="connecting" />;
    }
    return (
      <section className={EDITOR_SURFACE} data-connection-state="connecting">
        <div className={EDITOR_LOADING}>Opening this document…</div>
      </section>
    );
  }

  // No index row at all: the standalone harness routes, or a deep link into a
  // project whose index does not hold this document. Markdown is what this
  // route has always opened without a verdict, and the room itself is the next
  // thing to refuse if the link is wrong.
  if (resolution.state === 'unknown') {
    return <CollabEditorMount renderDecisionArtifact={renderDecisionArtifact} source={source} user={user} comments={comments} desktopHref={desktopHref} presentation={presentation} readOnly={readOnly} onHandleChange={onHandleChange} trackerReferences={trackerReferences} trackerReferenceAppearance="quiet" placedViewTypes={pagesPlacedViewTypes} />;
  }

  const { descriptor, state, reason } = resolution.presentation;
  if (state === 'ready' && descriptor?.editor.kind === 'lexical') {
    return <CollabEditorMount renderDecisionArtifact={renderDecisionArtifact} source={source} user={user} comments={comments} desktopHref={desktopHref} presentation={presentation} readOnly={readOnly} onHandleChange={onHandleChange} trackerReferences={trackerReferences} trackerReferenceAppearance="quiet" placedViewTypes={pagesPlacedViewTypes} />;
  }
  if (state === 'ready' && descriptor?.editor.kind === 'extension' && descriptor.editor.extensionId) {
    const extensionId = descriptor.editor.extensionId;
    return (
      <ExtensionEditorMount
        source={source}
        user={user}
        fileName={resolution.document.title}
        extensionId={extensionId}
        componentName={descriptor.editor.componentName ?? ''}
        // Names which of a bundle's codecs projects this document, which is
        // what decides whether source mode can be offered at all.
        documentType={descriptor.documentType}
        permissions={getBrowserExtension(extensionId)?.manifest.permissions}
        comments={comments}
        desktopHref={desktopHref}
        presentation={presentation}
        readOnly={readOnly}
        onViewportRegistered={onViewportRegistered}
        onHandleChange={onHandleChange}
      />
    );
  }

  if (presentation === 'inline') {
    // The compact refusal an inline surface uses everywhere else: a handoff
    // panel with a desktop button is longer than the box it would appear in.
    return (
      <div className="collab-editor-inline-unavailable" role="alert" data-docs-state="unsupported-document-type">
        This document cannot be shown here.
      </div>
    );
  }

  return (
    <section className={EDITOR_SURFACE} data-connection-state="offline">
      <DesktopHandoffPanel
        state="unsupported-document-type"
        title="This browser cannot open this document type"
        detail={reason}
        desktopHref={desktopHref}
      />
    </section>
  );
}
